// Advanced auto-crop utility for business cards using computer vision
export class SimpleAutoCrop {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.debugMode = false; // Set to true to see detection visualizations
  }

  initCanvas(width, height) {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
    }
    this.canvas.width = width;
    this.canvas.height = height;
    return this.canvas;
  }

  // Convert image to grayscale
  toGrayscale(imageData) {
    const data = imageData.data;
    const grayData = new Uint8Array(imageData.width * imageData.height);
    
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      grayData[i / 4] = gray;
    }
    
    return grayData;
  }

  // Gaussian blur for noise reduction
  gaussianBlur(grayData, width, height, sigma = 1.4) {
    const kernelSize = Math.ceil(sigma * 3) * 2 + 1;
    const kernel = this.createGaussianKernel(kernelSize, sigma);
    const output = new Uint8Array(grayData.length);
    
    const halfKernel = Math.floor(kernelSize / 2);
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        let weightSum = 0;
        
        for (let ky = -halfKernel; ky <= halfKernel; ky++) {
          for (let kx = -halfKernel; kx <= halfKernel; kx++) {
            const py = Math.max(0, Math.min(height - 1, y + ky));
            const px = Math.max(0, Math.min(width - 1, x + kx));
            const weight = kernel[ky + halfKernel][kx + halfKernel];
            
            sum += grayData[py * width + px] * weight;
            weightSum += weight;
          }
        }
        
        output[y * width + x] = sum / weightSum;
      }
    }
    
    return output;
  }

  createGaussianKernel(size, sigma) {
    const kernel = [];
    const center = Math.floor(size / 2);
    
    for (let y = 0; y < size; y++) {
      kernel[y] = [];
      for (let x = 0; x < size; x++) {
        const dx = x - center;
        const dy = y - center;
        kernel[y][x] = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
      }
    }
    
    return kernel;
  }

  // Advanced edge detection with Canny algorithm
  detectEdges(imageData) {
    const width = imageData.width;
    const height = imageData.height;
    
    // Step 1: Convert to grayscale
    const grayData = this.toGrayscale(imageData);
    
    // Step 2: Apply Gaussian blur to reduce noise
    const blurredData = this.gaussianBlur(grayData, width, height);
    
    // Step 3: Apply Sobel operator for gradient calculation
    const gradients = this.calculateGradients(blurredData, width, height);
    
    // Step 4: Non-maximum suppression
    const suppressed = this.nonMaximumSuppression(gradients, width, height);
    
    // Step 5: Double thresholding
    const edges = this.doubleThreshold(suppressed, width, height, 50, 100);
    
    // Convert back to ImageData format for compatibility
    const output = new Uint8ClampedArray(width * height * 4);
    for (let i = 0; i < edges.length; i++) {
      const value = edges[i];
      output[i * 4] = value;
      output[i * 4 + 1] = value;
      output[i * 4 + 2] = value;
      output[i * 4 + 3] = 255;
    }
    
    return new ImageData(output, width, height);
  }

  calculateGradients(grayData, width, height) {
    const sobelX = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]];
    const sobelY = [[-1, -2, -1], [0, 0, 0], [1, 2, 1]];
    
    const gradients = [];
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const pixel = grayData[(y + ky) * width + (x + kx)];
            gx += pixel * sobelX[ky + 1][kx + 1];
            gy += pixel * sobelY[ky + 1][kx + 1];
          }
        }
        
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        const direction = Math.atan2(gy, gx);
        
        gradients[y * width + x] = { magnitude, direction };
      }
    }
    
    return gradients;
  }

  nonMaximumSuppression(gradients, width, height) {
    const suppressed = new Array(width * height).fill(0);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const grad = gradients[idx];
        
        if (!grad) continue;
        
        const angle = grad.direction * 180 / Math.PI;
        let q = 255, r = 255;
        
        // Determine neighboring pixels based on gradient direction
        if ((angle >= -22.5 && angle <= 22.5) || (angle >= 157.5 || angle <= -157.5)) {
          q = gradients[idx - 1]?.magnitude || 0;
          r = gradients[idx + 1]?.magnitude || 0;
        } else if ((angle >= 22.5 && angle <= 67.5) || (angle >= -157.5 && angle <= -112.5)) {
          q = gradients[(y - 1) * width + x + 1]?.magnitude || 0;
          r = gradients[(y + 1) * width + x - 1]?.magnitude || 0;
        } else if ((angle >= 67.5 && angle <= 112.5) || (angle >= -112.5 && angle <= -67.5)) {
          q = gradients[(y - 1) * width + x]?.magnitude || 0;
          r = gradients[(y + 1) * width + x]?.magnitude || 0;
        } else if ((angle >= 112.5 && angle <= 157.5) || (angle >= -67.5 && angle <= -22.5)) {
          q = gradients[(y - 1) * width + x - 1]?.magnitude || 0;
          r = gradients[(y + 1) * width + x + 1]?.magnitude || 0;
        }
        
        if (grad.magnitude >= q && grad.magnitude >= r) {
          suppressed[idx] = grad.magnitude;
        }
      }
    }
    
    return suppressed;
  }

  doubleThreshold(suppressed, width, height, lowThreshold, highThreshold) {
    const strong = 255;
    const weak = 75;
    const edges = new Array(width * height).fill(0);
    
    for (let i = 0; i < suppressed.length; i++) {
      if (suppressed[i] >= highThreshold) {
        edges[i] = strong;
      } else if (suppressed[i] >= lowThreshold) {
        edges[i] = weak;
      }
    }
    
    // Edge tracking by hysteresis
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (edges[idx] === weak) {
          let hasStrongNeighbor = false;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (edges[(y + dy) * width + (x + dx)] === strong) {
                hasStrongNeighbor = true;
                break;
              }
            }
            if (hasStrongNeighbor) break;
          }
          edges[idx] = hasStrongNeighbor ? strong : 0;
        }
      }
    }
    
    return edges;
  }

  // Find contours in the edge-detected image
  findContours(edges, width, height) {
    const contours = [];
    const visited = new Array(width * height).fill(false);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (edges[idx] === 255 && !visited[idx]) {
          const contour = this.traceContour(edges, width, height, x, y, visited);
          if (contour.length > 20) { // Filter out small contours
            contours.push(contour);
          }
        }
      }
    }
    
    return contours;
  }

  traceContour(edges, width, height, startX, startY, visited) {
    const contour = [];
    const stack = [{ x: startX, y: startY }];
    
    while (stack.length > 0) {
      const { x, y } = stack.pop();
      const idx = y * width + x;
      
      if (x < 0 || x >= width || y < 0 || y >= height || visited[idx] || edges[idx] !== 255) {
        continue;
      }
      
      visited[idx] = true;
      contour.push({ x, y });
      
      // Check 8-connected neighbors
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          stack.push({ x: x + dx, y: y + dy });
        }
      }
    }
    
    return contour;
  }

  // Approximate polygon from contour using Douglas-Peucker algorithm
  approximatePolygon(contour, epsilon) {
    if (contour.length < 3) return contour;
    
    const result = this.douglasPeucker(contour, epsilon);
    return result;
  }

  douglasPeucker(points, epsilon) {
    if (points.length <= 2) return points;
    
    // Find the point with maximum distance from the line between first and last points
    let maxDist = 0;
    let maxIndex = 0;
    const start = points[0];
    const end = points[points.length - 1];
    
    for (let i = 1; i < points.length - 1; i++) {
      const dist = this.pointToLineDistance(points[i], start, end);
      if (dist > maxDist) {
        maxDist = dist;
        maxIndex = i;
      }
    }
    
    if (maxDist > epsilon) {
      const left = this.douglasPeucker(points.slice(0, maxIndex + 1), epsilon);
      const right = this.douglasPeucker(points.slice(maxIndex), epsilon);
      return left.slice(0, -1).concat(right);
    }
    
    return [start, end];
  }

  pointToLineDistance(point, lineStart, lineEnd) {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length === 0) return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
    
    return Math.abs(dy * point.x - dx * point.y + lineEnd.x * lineStart.y - lineEnd.y * lineStart.x) / length;
  }

  // Find rectangular contours that could be business cards
  findRectangularContours(contours, width, height) {
    const candidates = [];
    
    for (const contour of contours) {
      // Approximate the contour to a polygon
      const epsilon = 0.02 * Math.sqrt((contour[0]?.x - contour[contour.length - 1]?.x) ** 2 + 
                                       (contour[0]?.y - contour[contour.length - 1]?.y) ** 2);
      const polygon = this.approximatePolygon(contour, epsilon);
      
      // Check if it's roughly rectangular (4 corners)
      if (polygon.length >= 4 && polygon.length <= 8) {
        const boundingRect = this.getBoundingRect(polygon);
        const area = boundingRect.width * boundingRect.height;
        const aspectRatio = boundingRect.width / boundingRect.height;
        
        // Business card aspect ratio is typically 1.586:1 (3.5" x 2.125")
        const targetAspectRatio = 1.586;
        const aspectError = Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio;
        
        // Filter by size and aspect ratio
        if (area > (width * height * 0.05) && area < (width * height * 0.8) && aspectError < 0.5) {
          candidates.push({
            polygon,
            boundingRect,
            area,
            aspectRatio,
            aspectError,
            contourLength: contour.length
          });
        }
      }
    }
    
    return candidates.sort((a, b) => {
      // Score based on aspect ratio accuracy, size, and contour quality
      const scoreA = (1 - a.aspectError) * 0.5 + (a.area / (width * height)) * 0.3 + 
                     Math.min(1, a.contourLength / 100) * 0.2;
      const scoreB = (1 - b.aspectError) * 0.5 + (b.area / (width * height)) * 0.3 + 
                     Math.min(1, b.contourLength / 100) * 0.2;
      return scoreB - scoreA;
    });
  }

  getBoundingRect(polygon) {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    for (const point of polygon) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  // Simplified and more accurate business card detection
  findBusinessCardBounds(imageData) {
    const width = imageData.width;
    const height = imageData.height;

    console.log('🎯 Starting business card detection on image:', width, 'x', height);

    try {
      // Method 1: Try contrast-based detection first (most reliable)
      const contrastRect = this.findHighContrastRegion(imageData);
      if (contrastRect && this.isValidBusinessCard(contrastRect, width, height)) {
        console.log('🎯 Contrast-based detection successful:', contrastRect);
        return this.refineRectangle(contrastRect, width, height);
      }

      // Method 2: Try edge-based detection
      const edgeRect = this.findEdgeBasedRegion(imageData);
      if (edgeRect && this.isValidBusinessCard(edgeRect, width, height)) {
        console.log('🎯 Edge-based detection successful:', edgeRect);
        return this.refineRectangle(edgeRect, width, height);
      }

      // Method 3: Smart fallback with user guidance
      const smartFallback = this.createSmartFallback(width, height);
      console.log('🎯 Using smart fallback:', smartFallback);
      return smartFallback;
      
    } catch (error) {
      console.warn('🎯 Detection failed, using centered fallback:', error);
    }

    // Final fallback: Return a centered rectangle with business card aspect ratio
    const cardWidth = Math.min(width * 0.85, height * 0.85 * 1.586);
    const cardHeight = cardWidth / 1.586;
    
    return {
      x: (width - cardWidth) / 2,
      y: (height - cardHeight) / 2,
      width: cardWidth,
      height: cardHeight
    };
  }

  // Find regions with high contrast (business cards usually have text/graphics)
  findHighContrastRegion(imageData) {
    const width = imageData.width;
    const height = imageData.height;
    const data = imageData.data;
    
    // Create a grid to analyze image regions
    const gridSize = 32;
    const regionsX = Math.floor(width / gridSize);
    const regionsY = Math.floor(height / gridSize);
    const contrastMap = [];

    console.log('🎯 Analyzing', regionsX, 'x', regionsY, 'regions for contrast');

    // Analyze each grid region for contrast
    for (let ry = 0; ry < regionsY; ry++) {
      contrastMap[ry] = [];
      for (let rx = 0; rx < regionsX; rx++) {
        const startX = rx * gridSize;
        const startY = ry * gridSize;
        const endX = Math.min(startX + gridSize, width);
        const endY = Math.min(startY + gridSize, height);
        
        let min = 255, max = 0, sum = 0, count = 0;
        
        for (let y = startY; y < endY; y++) {
          for (let x = startX; x < endX; x++) {
            const idx = (y * width + x) * 4;
            const gray = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
            min = Math.min(min, gray);
            max = Math.max(max, gray);
            sum += gray;
            count++;
          }
        }
        
        const contrast = max - min;
        const brightness = sum / count;
        contrastMap[ry][rx] = { contrast, brightness, x: startX, y: startY };
      }
    }

    // Find rectangular regions with consistent high contrast
    const candidates = this.findRectangularHighContrastRegions(contrastMap, gridSize);
    
    if (candidates.length > 0) {
      // Score candidates based on aspect ratio, size, and contrast
      candidates.sort((a, b) => {
        const aspectRatioA = a.width / a.height;
        const aspectRatioB = b.width / b.height;
        const targetAspect = 1.586;
        
        const aspectScoreA = 1 - Math.abs(aspectRatioA - targetAspect) / targetAspect;
        const aspectScoreB = 1 - Math.abs(aspectRatioB - targetAspect) / targetAspect;
        
        const sizeScoreA = (a.width * a.height) / (width * height);
        const sizeScoreB = (b.width * b.height) / (width * height);
        
        const totalScoreA = aspectScoreA * 0.6 + sizeScoreA * 0.3 + a.contrast * 0.1;
        const totalScoreB = aspectScoreB * 0.6 + sizeScoreB * 0.3 + b.contrast * 0.1;
        
        return totalScoreB - totalScoreA;
      });
      
      console.log('🎯 Found', candidates.length, 'high contrast candidates');
      return candidates[0];
    }

    return null;
  }

  findRectangularHighContrastRegions(contrastMap, gridSize) {
    const regions = [];
    const threshold = 30; // Minimum contrast threshold
    
    // Find connected regions of high contrast
    for (let ry = 0; ry < contrastMap.length - 2; ry++) {
      for (let rx = 0; rx < contrastMap[ry].length - 2; rx++) {
        // Check if this could be the top-left of a business card region
        const region = this.expandContrastRegion(contrastMap, rx, ry, threshold, gridSize);
        if (region && region.width > gridSize * 3 && region.height > gridSize * 2) {
          regions.push(region);
        }
      }
    }
    
    return regions;
  }

  expandContrastRegion(contrastMap, startX, startY, threshold, gridSize) {
    let minX = startX, maxX = startX;
    let minY = startY, maxY = startY;
    let totalContrast = 0, count = 0;
    
    // Check how far we can expand this region
    for (let ry = startY; ry < Math.min(startY + 8, contrastMap.length); ry++) {
      for (let rx = startX; rx < Math.min(startX + 12, contrastMap[ry].length); rx++) {
        const cell = contrastMap[ry][rx];
        if (cell.contrast > threshold) {
          minX = Math.min(minX, rx);
          maxX = Math.max(maxX, rx);
          minY = Math.min(minY, ry);
          maxY = Math.max(maxY, ry);
          totalContrast += cell.contrast;
          count++;
        }
      }
    }
    
    if (count < 6) return null; // Too small
    
    return {
      x: minX * gridSize,
      y: minY * gridSize,
      width: (maxX - minX + 1) * gridSize,
      height: (maxY - minY + 1) * gridSize,
      contrast: totalContrast / count
    };
  }

  // Simplified edge detection for card boundaries  
  findEdgeBasedRegion(imageData) {
    const width = imageData.width;
    const height = imageData.height;
    const data = imageData.data;
    
    // Find strong horizontal and vertical edges
    const edges = this.findStrongEdges(data, width, height);
    
    // Look for rectangular patterns
    const rect = this.findLargestRectangle(edges, width, height);
    
    return rect;
  }

  findStrongEdges(data, width, height) {
    const edges = new Uint8Array(width * height);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const pixelIdx = idx * 4;
        
        // Get surrounding pixels
        const left = data[(idx - 1) * 4] * 0.299 + data[(idx - 1) * 4 + 1] * 0.587 + data[(idx - 1) * 4 + 2] * 0.114;
        const right = data[(idx + 1) * 4] * 0.299 + data[(idx + 1) * 4 + 1] * 0.587 + data[(idx + 1) * 4 + 2] * 0.114;
        const top = data[(idx - width) * 4] * 0.299 + data[(idx - width) * 4 + 1] * 0.587 + data[(idx - width) * 4 + 2] * 0.114;
        const bottom = data[(idx + width) * 4] * 0.299 + data[(idx + width) * 4 + 1] * 0.587 + data[(idx + width) * 4 + 2] * 0.114;
        
        // Calculate edge strength
        const horizontalEdge = Math.abs(left - right);
        const verticalEdge = Math.abs(top - bottom);
        const edgeStrength = Math.max(horizontalEdge, verticalEdge);
        
        edges[idx] = edgeStrength > 40 ? 255 : 0;
      }
    }
    
    return edges;
  }

  findLargestRectangle(edges, width, height) {
    // Find the largest rectangular region with strong edges
    let bestRect = null;
    let bestScore = 0;
    
    // Sample potential rectangles
    const step = Math.min(width, height) / 20;
    
    for (let y = 0; y < height - step * 4; y += step) {
      for (let x = 0; x < width - step * 6; x += step) {
        for (let w = step * 6; w < width - x && w < step * 15; w += step) {
          const h = w / 1.586; // Business card aspect ratio
          if (y + h >= height) continue;
          
          const rect = { x, y, width: w, height: h };
          const score = this.scoreRectangleEdges(edges, rect, width, height);
          
          if (score > bestScore) {
            bestScore = score;
            bestRect = rect;
          }
        }
      }
    }
    
    return bestScore > 0.3 ? bestRect : null;
  }

  scoreRectangleEdges(edges, rect, imageWidth, imageHeight) {
    const { x, y, width, height } = rect;
    let edgeCount = 0;
    let totalSamples = 0;
    
    // Sample edges around the rectangle perimeter
    const sampleCount = 20;
    
    // Top and bottom edges
    for (let i = 0; i < sampleCount; i++) {
      const sampleX = Math.floor(x + (width * i) / sampleCount);
      if (sampleX >= 0 && sampleX < imageWidth) {
        // Top edge
        const topY = Math.floor(y);
        if (topY >= 0 && topY < imageHeight) {
          edgeCount += edges[topY * imageWidth + sampleX] > 0 ? 1 : 0;
          totalSamples++;
        }
        // Bottom edge
        const bottomY = Math.floor(y + height);
        if (bottomY >= 0 && bottomY < imageHeight) {
          edgeCount += edges[bottomY * imageWidth + sampleX] > 0 ? 1 : 0;
          totalSamples++;
        }
      }
    }
    
    // Left and right edges
    for (let i = 0; i < sampleCount; i++) {
      const sampleY = Math.floor(y + (height * i) / sampleCount);
      if (sampleY >= 0 && sampleY < imageHeight) {
        // Left edge
        const leftX = Math.floor(x);
        if (leftX >= 0 && leftX < imageWidth) {
          edgeCount += edges[sampleY * imageWidth + leftX] > 0 ? 1 : 0;
          totalSamples++;
        }
        // Right edge
        const rightX = Math.floor(x + width);
        if (rightX >= 0 && rightX < imageWidth) {
          edgeCount += edges[sampleY * imageWidth + rightX] > 0 ? 1 : 0;
          totalSamples++;
        }
      }
    }
    
    return totalSamples > 0 ? edgeCount / totalSamples : 0;
  }

  // Validate if a rectangle looks like a business card
  isValidBusinessCard(rect, imageWidth, imageHeight) {
    const aspectRatio = rect.width / rect.height;
    const targetAspectRatio = 1.586;
    const aspectError = Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio;
    
    const area = rect.width * rect.height;
    const imageArea = imageWidth * imageHeight;
    const areaRatio = area / imageArea;
    
    // Business card should be:
    // 1. Proper aspect ratio (within 40% tolerance)
    // 2. Reasonable size (between 10% and 80% of image)
    // 3. Not touching the edges (some padding)
    return aspectError < 0.4 && 
           areaRatio > 0.1 && areaRatio < 0.8 &&
           rect.x > imageWidth * 0.05 && 
           rect.y > imageHeight * 0.05 &&
           rect.x + rect.width < imageWidth * 0.95 &&
           rect.y + rect.height < imageHeight * 0.95;
  }

  // Create a smart fallback that's more likely to contain the business card
  createSmartFallback(width, height) {
    // Create a rectangle that's likely to contain a business card
    // Slightly smaller than full image, proper aspect ratio, centered
    const maxCardWidth = Math.min(width * 0.9, height * 0.9 * 1.586);
    const cardHeight = maxCardWidth / 1.586;
    
    // Center it but bias slightly upward (business cards often in upper portion)
    const x = (width - maxCardWidth) / 2;
    const y = Math.max(height * 0.1, (height - cardHeight) / 2 - height * 0.1);
    
    return {
      x: Math.floor(x),
      y: Math.floor(y),
      width: Math.floor(maxCardWidth),
      height: Math.floor(cardHeight)
    };
  }

  // Refine rectangle bounds to ensure they're within image bounds and properly sized
  refineRectangle(rect, imageWidth, imageHeight) {
    const padding = 10;
    
    // Ensure minimum size
    const minWidth = Math.min(200, imageWidth * 0.3);
    const minHeight = minWidth / 1.586;
    
    let { x, y, width, height } = rect;
    
    // Ensure minimum dimensions
    if (width < minWidth) {
      const diff = minWidth - width;
      x = Math.max(0, x - diff / 2);
      width = minWidth;
    }
    
    if (height < minHeight) {
      const diff = minHeight - height;
      y = Math.max(0, y - diff / 2);
      height = minHeight;
    }
    
    // Ensure bounds are within image
    x = Math.max(padding, Math.min(x, imageWidth - width - padding));
    y = Math.max(padding, Math.min(y, imageHeight - height - padding));
    width = Math.min(width, imageWidth - x - padding);
    height = Math.min(height, imageHeight - y - padding);
    
    return { x, y, width, height };
  }

  // Color-based region detection as fallback
  findColorBasedRegion(imageData) {
    const width = imageData.width;
    const height = imageData.height;
    const data = imageData.data;
    
    // Find regions with high contrast (potential card boundaries)
    const blockSize = 20;
    const contrastThreshold = 30;
    let bestRegion = null;
    let bestScore = 0;
    
    for (let y = 0; y < height - blockSize; y += blockSize) {
      for (let x = 0; x < width - blockSize; x += blockSize) {
        // Calculate variance in this block
        let sum = 0, sumSq = 0, count = 0;
        
        for (let by = 0; by < blockSize && y + by < height; by++) {
          for (let bx = 0; bx < blockSize && x + bx < width; bx++) {
            const idx = ((y + by) * width + (x + bx)) * 4;
            const gray = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
            sum += gray;
            sumSq += gray * gray;
            count++;
          }
        }
        
        const mean = sum / count;
        const variance = (sumSq / count) - (mean * mean);
        
        if (variance > contrastThreshold) {
          // Expand this region to find the full card
          const expandedRegion = this.expandRegion(data, width, height, x, y, blockSize);
          
          if (expandedRegion) {
            const aspectRatio = expandedRegion.width / expandedRegion.height;
            const targetAspectRatio = 1.586;
            const aspectScore = 1 - Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio;
            const sizeScore = (expandedRegion.width * expandedRegion.height) / (width * height);
            
            const score = aspectScore * 0.6 + sizeScore * 0.4;
            
            if (score > bestScore && score > 0.3) {
              bestScore = score;
              bestRegion = expandedRegion;
            }
          }
        }
      }
    }
    
    return bestRegion;
  }

  expandRegion(data, width, height, startX, startY, initialSize) {
    // Simple region growing algorithm
    const visited = new Array(width * height).fill(false);
    const queue = [];
    const region = [];
    
    // Get reference color from the center of initial block
    const refIdx = ((startY + initialSize / 2) * width + (startX + initialSize / 2)) * 4;
    const refR = data[refIdx];
    const refG = data[refIdx + 1];
    const refB = data[refIdx + 2];
    
    queue.push({ x: startX + initialSize / 2, y: startY + initialSize / 2 });
    
    const colorThreshold = 40;
    
    while (queue.length > 0 && region.length < 5000) { // Limit region size
      const { x, y } = queue.shift();
      const idx = y * width + x;
      
      if (x < 0 || x >= width || y < 0 || y >= height || visited[idx]) {
        continue;
      }
      
      const pixelIdx = idx * 4;
      const r = data[pixelIdx];
      const g = data[pixelIdx + 1];
      const b = data[pixelIdx + 2];
      
      const colorDiff = Math.sqrt((r - refR) ** 2 + (g - refG) ** 2 + (b - refB) ** 2);
      
      if (colorDiff < colorThreshold) {
        visited[idx] = true;
        region.push({ x, y });
        
        // Add neighbors
        queue.push({ x: x + 1, y }, { x: x - 1, y }, { x, y: y + 1 }, { x, y: y - 1 });
      }
    }
    
    if (region.length < 100) return null;
    
    // Calculate bounding rectangle
    return this.getBoundingRect(region);
  }

  // Add debug visualization capability
  createDebugVisualization(imageData, detectedBounds) {
    if (!this.debugMode) return null;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    
    // Draw original image
    ctx.putImageData(imageData, 0, 0);
    
    // Overlay detected bounds
    if (detectedBounds) {
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 3;
      ctx.strokeRect(detectedBounds.x, detectedBounds.y, detectedBounds.width, detectedBounds.height);
      
      // Add label
      ctx.fillStyle = '#00ff00';
      ctx.font = '16px Arial';
      ctx.fillText('Detected Card', detectedBounds.x, detectedBounds.y - 5);
    }
    
    return canvas.toDataURL();
  }

  // Enable/disable debug mode
  setDebugMode(enabled) {
    this.debugMode = enabled;
  }

  // Apply perspective correction (simple deskewing)
  correctPerspective(canvas, bounds, corners = null) {
    if (!corners || corners.length !== 4) {
      // No perspective correction needed, use simple crop
      return this.simpleCrop(canvas, bounds);
    }
    
    // For now, use simple bounds-based cropping
    // In a full implementation, you would implement perspective transformation here
    return this.simpleCrop(canvas, bounds);
  }

  simpleCrop(sourceCanvas, bounds) {
    const croppedCanvas = document.createElement('canvas');
    const ctx = croppedCanvas.getContext('2d');
    
    croppedCanvas.width = bounds.width;
    croppedCanvas.height = bounds.height;
    
    ctx.drawImage(
      sourceCanvas,
      bounds.x, bounds.y, bounds.width, bounds.height,
      0, 0, bounds.width, bounds.height
    );
    
    return croppedCanvas;
  }

  // Enhanced business card detection and cropping from video
  async cropBusinessCard(videoElement, customBounds = null) {
    if (!videoElement) return null;

    try {
      const width = videoElement.videoWidth || videoElement.offsetWidth;
      const height = videoElement.videoHeight || videoElement.offsetHeight;
      
      if (width === 0 || height === 0) {
        console.warn('Video has no dimensions');
        return null;
      }

      console.log('🎯 Starting business card detection on video:', width, 'x', height);

      // Create canvas and draw video frame
      const canvas = this.initCanvas(width, height);
      this.ctx.drawImage(videoElement, 0, 0, width, height);
      
      // Get image data for processing
      const imageData = this.ctx.getImageData(0, 0, width, height);
      
      // Find business card bounds using advanced algorithm
      const bounds = customBounds || this.findBusinessCardBounds(imageData);
      
      console.log('🎯 Detected bounds:', bounds);
      
      // Create debug visualization if enabled
      const debugImage = this.createDebugVisualization(imageData, bounds);
      if (debugImage) {
        console.log('🐛 Debug visualization created');
      }
      
      // Crop the detected region
      const croppedCanvas = this.simpleCrop(canvas, bounds);
      
      // Calculate confidence score based on detection quality
      const confidence = this.calculateConfidence(bounds, width, height);
      
      // Convert to blob and data URL
      return new Promise((resolve) => {
        croppedCanvas.toBlob((blob) => {
          resolve({
            canvas: croppedCanvas,
            blob: blob,
            dataUrl: croppedCanvas.toDataURL('image/jpeg', 0.9),
            bounds: bounds,
            confidence: confidence,
            debugImage: debugImage,
            originalSize: { width, height }
          });
        }, 'image/jpeg', 0.9);
      });
    } catch (error) {
      console.error('🎯 Auto-crop error:', error);
      return null;
    }
  }

  calculateConfidence(bounds, imageWidth, imageHeight) {
    const area = bounds.width * bounds.height;
    const imageArea = imageWidth * imageHeight;
    const areaRatio = area / imageArea;
    
    const aspectRatio = bounds.width / bounds.height;
    const targetAspectRatio = 1.586;
    const aspectError = Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio;
    
    // Higher confidence for better aspect ratio and reasonable size
    const aspectScore = Math.max(0, 1 - aspectError);
    const sizeScore = Math.min(1, areaRatio / 0.4);
    
    return Math.min(0.95, (aspectScore * 0.6 + sizeScore * 0.4) * 0.9 + 0.1);
  }

  // Enhanced crop from base64 image
  async cropFromBase64(base64Image, customBounds = null) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const width = img.width;
          const height = img.height;
          
          console.log('🎯 Starting business card detection on base64 image:', width, 'x', height);
          
          // Create canvas and draw image
          const sourceCanvas = this.initCanvas(width, height);
          this.ctx.drawImage(img, 0, 0, width, height);
          
          // Get image data for processing
          const imageData = this.ctx.getImageData(0, 0, width, height);
          
          // Find business card bounds using advanced algorithm
          const bounds = customBounds || this.findBusinessCardBounds(imageData);
          
          console.log('🎯 Detected bounds:', bounds);
          
          // Create debug visualization if enabled
          const debugImage = this.createDebugVisualization(imageData, bounds);
          if (debugImage) {
            console.log('🐛 Debug visualization created');
          }
          
          // Crop the detected region
          const croppedCanvas = this.simpleCrop(sourceCanvas, bounds);
          
          // Calculate confidence score
          const confidence = this.calculateConfidence(bounds, width, height);
          
          // Convert to blob and data URL
          croppedCanvas.toBlob((blob) => {
            resolve({
              canvas: croppedCanvas,
              blob: blob,
              dataUrl: croppedCanvas.toDataURL('image/jpeg', 0.9),
              bounds: bounds,
              confidence: confidence,
              debugImage: debugImage,
              originalSize: { width, height }
            });
          }, 'image/jpeg', 0.9);
        } catch (error) {
          console.error('🎯 Crop from base64 error:', error);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.error('🎯 Failed to load image for cropping');
        resolve(null);
      };
      img.src = base64Image;
    });
  }
}

export default SimpleAutoCrop;